import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RoleChip, roleByWord, roleLabel, RolePickerSheet, type Role } from '../components/RolePicker.js';
import { setMessageRole, type RoleBy } from '../lib/projectJobs.js';
import { createTask, getInbox, getMessage, getThread, KEEPALIVE, listProjects, triageMessage, updateTask } from '../lifeos/api.js';
import { attachmentsOf, contactNameOf, contactSphereOf, saveAttachmentToProject, type AttachmentInfo } from '../lib/connectors.js';
import { ageLabel, firstLine, prettyHandle, stamp, taskCode } from '../lifeos/format.js';
import { channelLabel } from '../lifeos/timeline.js';
import { sphereColor, sphereName, useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { useBusy } from '../lifeos/useBusy.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { InboxCounts, InboxItem, InboxStatus, MessageRow, Project, Sphere, TriageStatus } from '../lifeos/types.js';
import { Button, EmptyState, ErrorState, Group, Icon, Loader, MoreRow, Page, PickSheet, Row, Segmented, Sheet, type IconName, type SwipeAction, Avatar, useParamSetter, useSphereLens } from '../ui/index.js';
import { Loading } from '../lifeos/ui.js';

/** "Inbox" means messages only; tasks with status inbox are "To sort" (spec §3.2). */
const STATUSES: readonly { value: InboxStatus; label: string; count: keyof InboxCounts; empty: string; hint?: string; icon: IconName }[] = [
  { value: 'needs_reply', label: 'Needs reply', count: 'needsReply', empty: 'Nobody is waiting on you.', hint: 'Replies you owe land here.', icon: 'check' },
  { value: 'new', label: 'New', count: 'new', empty: 'Nothing new to triage.', icon: 'inbox' },
  { value: 'waiting', label: 'Waiting', count: 'waiting', empty: 'No threads waiting on others.', icon: 'hourglass' },
  { value: 'fyi', label: 'FYI', count: 'fyi', empty: 'No FYIs.', icon: 'archive' },
];

/** Who a message is from, in the order a person would recognise them. Display only, on this page. */
export function senderName(item: InboxItem): string {
  return contactNameOf(item) ?? prettyHandle(item.message.fromName ?? item.message.handle) ?? 'unknown sender';
}

/**
 * An unverified email is shown by its address, not by the From name: the
 * sender picks that name ("Harbor Light Dental Billing"), so as a headline it
 * would vouch for whoever typed it. The name stays visible as a claim.
 */
export function showsAddress(item: InboxItem): boolean {
  return !item.senderVerified && item.message.channel === 'email';
}

/** The row's headline: the address for an unverified email, else the sender's name. */
export function headlineName(item: InboxItem): string {
  return showsAddress(item) ? prettyHandle(item.message.handle) || 'unknown sender' : senderName(item);
}

/**
 * "Urgent" is a display badge, so it is kept for what is really urgent:
 * rules urgency 3 (an urgent word plus an important contact or family), or
 * ≥ 2 when the server says the rules found urgent words. The urgency values
 * themselves are untouched; Today and the inbox sweep still read ≥ 2.
 */
export function isUrgent(m: MessageRow): boolean {
  if (m.urgency >= 3) return true;
  const words = m.meta && typeof m.meta === 'object' ? (m.meta as Record<string, unknown>).urgentWords : undefined;
  return m.urgency >= 2 && Array.isArray(words) && words.length > 0;
}

const UNVERIFIED_REPLY = 'Unverified sender — check the address before replying';

/**
 * The one hint line a row may carry, or null. A plain "reply" suggestion adds
 * nothing to a list called Needs reply, so only a task or agent-run
 * suggestion shows, and the unverified-sender warning before any reply.
 */
export function inboxHint(item: InboxItem): { text: string; tone: 'warn' | 'muted'; approvalId: string | null } | null {
  const s = item.suggested;
  if (s.action === 'reply') {
    if (item.senderVerified) return null;
    return { text: /^unverified/i.test(s.reason) ? s.reason : UNVERIFIED_REPLY, tone: 'warn', approvalId: null };
  }
  if (s.action === 'task' || s.action === 'agent_run') {
    const meta = item.message.meta && typeof item.message.meta === 'object' ? (item.message.meta as Record<string, unknown>) : {};
    const approvalId = s.action === 'agent_run' && typeof meta.proposalApprovalId === 'string' ? meta.proposalApprovalId : null;
    const text = s.reason || (s.action === 'task' ? 'Could be a task' : 'Looks like a website change request');
    return { text, tone: 'muted', approvalId };
  }
  return null;
}

/**
 * The task title "Make task" creates: the contact's own display name only,
 * never the subject, From name, handle or text. Task titles travel on as
 * trusted text (Signal reminders, briefs, MCP results), and inbound text may
 * only reach those under `untrusted_text` (spec §5.2, §7). The message itself
 * stays one tap away through `sourceRef`.
 */
export function replyTaskTitle(item: InboxItem): string {
  return firstLine(`Reply to ${item.contact?.name ?? 'unknown sender'}`, 120);
}

/**
 * The messages a Done/FYI on a row closes. A row is only its thread's newest
 * match, so closing that one message alone lets an older unanswered one bring
 * the thread straight back. This takes the row plus every earlier inbound
 * message in the thread that would still surface it (untriaged, needs reply,
 * or the status of the view the row is in, unless already `to`), and nothing
 * newer than the row: the user has not seen that. If the thread can't be
 * read, the row alone.
 */
export async function threadMessageIds(m: MessageRow, view: InboxStatus, to: TriageStatus): Promise<number[]> {
  if (!m.threadKey) return [m.id];
  const open = new Set<TriageStatus>(['new', 'needs_reply', view]);
  open.delete(to);
  const upTo = Date.parse(m.sentAt);
  try {
    const earlier = (await getThread(m.threadKey)).filter(
      (x) => x.id !== m.id && x.direction === 'in' && open.has(x.triageStatus) && Date.parse(x.sentAt) <= upTo,
    );
    return [m.id, ...earlier.map((x) => x.id)];
  } catch {
    return [m.id];
  }
}

/** A message's role as shown: the owner's flip on this page wins over the loaded row. */
export interface RoleView {
  sphereKey: string | null;
  projectId: string | null;
  roleBy: RoleBy;
}

export function roleOf(m: MessageRow, override?: RoleView): RoleView {
  if (override) return override;
  const by = (m as MessageRow & { roleBy?: RoleBy }).roleBy ?? null;
  return { sphereKey: m.sphereKey, projectId: m.projectId, roleBy: by };
}

/**
 * A row from elsewhere (a project's "To reply") opened here by `?open=<id>`:
 * the minimal inbox item for the thread sheet. Sender counts as verified only
 * when the message is linked to a known contact.
 */
export function itemFromMessage(m: MessageRow, now = Date.now()): InboxItem {
  return {
    message: m,
    contact: null,
    suggested: { action: 'none', reason: '' },
    senderVerified: m.contactId !== null,
    ageHours: Math.max(0, (now - Date.parse(m.sentAt)) / 3_600_000),
  };
}

/** Threads shown before "Show N more" — a short list is one you finish. */
export const INBOX_SHOWN = 5;

/** The header line: what is waiting, in words, zeros left out. */
export function inboxSub(c: InboxCounts | undefined): string | undefined {
  if (!c) return undefined;
  const parts = [
    c.needsReply > 0 ? `${c.needsReply} need${c.needsReply === 1 ? 's' : ''} a reply` : null,
    c.new > 0 ? `${c.new} new` : null,
    c.waiting > 0 ? `${c.waiting} waiting` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'All caught up';
}

/**
 * Inbox (`/inbox`, spec §6): one row per thread, Mail-style. Done is the one
 * visible action (and the full right swipe); FYI and Make task sit behind a
 * left swipe or the ⋯ sheet; tapping the row opens the thread in a sheet.
 * Message text is third-party content: plain text only, never markup.
 */
export default function Inbox(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const status = STATUSES.find((s) => s.value === params.get('status'))?.value ?? 'needs_reply';
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const inbox = useLoad(() => getInbox({ status, sphere: sphere ?? undefined }), [status, sphere]);
  const toast = useToast();
  const making = useBusy<number>();
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const [menu, setMenu] = useState<InboxItem | null>(null);
  const [open, setOpen] = useState<InboxItem | null>(null);
  const [all, setAll] = useState(false);
  const projectList = useLoad(() => listProjects().catch((): Project[] => []), []);
  const projects = projectList.data ?? [];
  const [roles, setRoles] = useState<ReadonlyMap<number, RoleView>>(new Map());
  const [picking, setPicking] = useState<InboxItem | null>(null);
  const location = useLocation();
  const openId = params.get('open');

  // `?open=<id>` (from a project's To reply): open that thread once it can be found.
  useEffect(() => {
    if (!openId) return;
    const id = Number(openId);
    const found = inbox.data?.items.find((i) => i.message.id === id);
    const passed = (location.state as { message?: MessageRow } | null)?.message;
    if (found) setOpen(found);
    else if (passed && passed.id === id) setOpen(itemFromMessage(passed));
    else if (!inbox.data) return;
    else if (Number.isSafeInteger(id) && id > 0) {
      // Not in this view (search landing): fetch it and open the thread.
      getMessage(id).then(
        (m) => setOpen(itemFromMessage(m)),
        (e) => toast.error(errorText(e)),
      );
    }
    setParam('open', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, inbox.data]);

  function setRole(item: InboxItem, role: Role): void {
    const m = item.message;
    const before = roles.get(m.id);
    setPicking(null);
    setRoles((r) => new Map(r).set(m.id, { ...role, roleBy: 'owner' }));
    setMessageRole(m.id, role).then(
      () => toast.info(`About: ${roleLabel(role, spheres, projects)}`),
      (err: unknown) => {
        setRoles((r) => {
          const n = new Map(r);
          if (before) n.set(m.id, before);
          else n.delete(m.id);
          return n;
        });
        toast.error(`Couldn't change it: ${errorText(err)}`);
      },
    );
  }

  const unhide = (id: number) => (): void =>
    setHidden((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });

  function triage(item: InboxItem, to: 'done' | 'fyi'): void {
    const id = item.message.id;
    // Read the thread now, so the commit at the end of the window is only writes
    // (it may run during page unload, where only keepalive requests survive).
    const ids = threadMessageIds(item.message, status, to);
    setHidden((s) => new Set(s).add(id));
    toast.defer({
      text: to === 'done' ? `Done: ${headlineName(item)}` : `Marked FYI: ${headlineName(item)}`,
      commit: async () => {
        await Promise.all((await ids).map((x) => triageMessage(x, { status: to }, KEEPALIVE)));
      },
      onUndo: unhide(id),
      onDone: () => void inbox.reload().finally(unhide(id)),
      onError: unhide(id),
      failText: "Couldn't triage that",
    });
  }

  function makeTask(item: InboxItem): Promise<void> {
    return making.run(item.message.id, async () => {
      try {
        const task = await createTask({
          title: replyTaskTitle(item),
          contactId: item.contact?.id ?? item.message.contactId ?? null,
          source: 'message',
          sourceRef: String(item.message.id),
        });
        toast.undoable({
          text: `Added ${taskCode(task.shortCode)} ${task.title}`,
          undo: () => updateTask(task.id, { status: 'dropped', repeat: null }),
        });
      } catch (err) {
        toast.error(`Couldn't make a task: ${errorText(err)}`);
      }
    });
  }

  /** Close whichever sheet is up, then act (a double tap can't act twice). */
  const act = (item: InboxItem, what: 'done' | 'fyi' | 'task'): void => {
    setMenu(null);
    setOpen(null);
    if (what === 'task') void makeTask(item);
    else triage(item, what);
  };

  const counts = inbox.data?.counts;
  const meta = STATUSES.find((s) => s.value === status)!;
  return (
    <Page title="Inbox" sub={inboxSub(counts)} actions={lens.button} className="ibx">
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Segmented
        label="Inbox filter"
        value={status}
        options={STATUSES.map((s) => ({ value: s.value, label: s.label, count: counts?.[s.count] }))}
        onChange={(v) => {
          setAll(false);
          setParam('status', v === 'needs_reply' ? null : v);
        }}
      />
      <Loader state={inbox}>
        {(d) => {
          const rows = d.items.filter((i) => !hidden.has(i.message.id));
          if (rows.length === 0) return <EmptyState icon={meta.icon} title={meta.empty} hint={meta.hint} />;
          const shown = all ? rows : rows.slice(0, INBOX_SHOWN);
          return (
            <Group label={meta.label}>
              {shown.map((item) => (
                <InboxRow
                  key={item.message.id}
                  item={item}
                  spheres={spheres}
                  projects={projects}
                  role={roleOf(item.message, roles.get(item.message.id))}
                  onOpen={() => setOpen(item)}
                  onDone={() => triage(item, 'done')}
                  onFyi={status === 'fyi' ? undefined : () => triage(item, 'fyi')}
                  onTask={() => void makeTask(item)}
                  onMenu={() => setMenu(item)}
                />
              ))}
              <MoreRow count={rows.length - shown.length} onClick={() => setAll(true)} />
            </Group>
          );
        }}
      </Loader>
      {menu && (
        <Sheet title={headlineName(menu)} onClose={() => setMenu(null)}>
          <div className="lo-sheet__list lo-sheet__list--first">
            <button type="button" className="lo-sheet__row" onClick={() => act(menu, 'fyi')}>
              FYI
              <span className="lo-sheet__rowhint">no reply needed</span>
            </button>
            <button type="button" className="lo-sheet__row" disabled={making.busy.has(menu.message.id)} onClick={() => act(menu, 'task')}>
              Make task
              <span className="lo-sheet__rowhint">reply later, from Tasks</span>
            </button>
            <button
              type="button"
              className="lo-sheet__row"
              onClick={() => {
                setPicking(menu);
                setMenu(null);
              }}
            >
              Personal or work?
              <span className="lo-sheet__rowhint">{roleLabel(roleOf(menu.message, roles.get(menu.message.id)), spheres, projects)}</span>
            </button>
            <ReviewLink item={menu} />
          </div>
        </Sheet>
      )}
      {lens.sheet}
      {open && (
        <ThreadSheet
          item={open}
          onClose={() => setOpen(null)}
          onAct={(what) => act(open, what)}
          busy={making.busy.has(open.message.id)}
          role={
            <RoleBlock
              role={roleOf(open.message, roles.get(open.message.id))}
              spheres={spheres}
              projects={projects}
              onClick={() => setPicking(open)}
            />
          }
        />
      )}
      {picking && (
        <RolePickerSheet
          current={(() => {
            const r = roleOf(picking.message, roles.get(picking.message.id));
            return r.sphereKey ? { sphereKey: r.sphereKey, projectId: r.projectId } : null;
          })()}
          contactId={picking.contact?.id ?? picking.message.contactId}
          spheres={spheres}
          projects={projects}
          onPick={(r) => setRole(picking, r)}
          onClose={() => setPicking(null)}
        />
      )}
    </Page>
  );
}

/** An agent proposal waiting on this message: one tap to review it. */
function ReviewLink({ item }: { item: InboxItem }): JSX.Element | null {
  const hint = inboxHint(item);
  if (!hint?.approvalId) return null;
  return (
    <Link className="lo-sheet__row ibx__review" to={`/agents?approval=${encodeURIComponent(hint.approvalId)}`}>
      Review proposal
      <span className="lo-sheet__rowhint">in Agents</span>
    </Link>
  );
}

/** In the thread sheet: the role chip (tap to change) and who set it. */
function RoleBlock({
  role,
  spheres,
  projects,
  onClick,
}: {
  role: RoleView;
  spheres: Sphere[];
  projects: Project[];
  onClick: () => void;
}): JSX.Element {
  return (
    <p className="ibx-role">
      <span className="ibx-role__label">About</span>
      <RoleChip role={role} roleBy={role.roleBy} spheres={spheres} projects={projects} onClick={onClick} />
      <span className="ibx-role__by">{role.sphereKey || role.projectId ? roleByWord(role.roleBy) : 'tap to file it'}</span>
    </p>
  );
}

function InboxRow({
  item,
  spheres,
  projects,
  role,
  onOpen,
  onDone,
  onFyi,
  onTask,
  onMenu,
}: {
  item: InboxItem;
  spheres: Sphere[];
  projects: Project[];
  role: RoleView;
  onOpen: () => void;
  onDone: () => void;
  onFyi?: () => void;
  onTask: () => void;
  onMenu: () => void;
}): JSX.Element {
  const m = item.message;
  const preview = m.subject?.trim() || firstLine(m.text, 160);
  const address = showsAddress(item);
  const who = headlineName(item);
  const claimed = address && m.fromName?.trim() && m.fromName.trim() !== m.handle ? m.fromName.trim() : null;
  const hint = inboxHint(item);
  const job = contactSphereOf(item);
  const color = sphereColor(spheres, role.sphereKey ?? job);
  const hasRole = Boolean(role.sphereKey || role.projectId);
  const trailing: SwipeAction[] = [
    ...(onFyi ? [{ label: 'FYI', icon: 'archive' as const, tone: 'neutral' as const, onAction: onFyi }] : []),
    { label: 'Task', icon: 'tasks', tone: 'accent', onAction: onTask },
  ];

  return (
    <Row
      className={`ibx-row${address ? ' ibx-row--unverified' : ''}`}
      leading={<Avatar name={who} color={color} icon={address ? 'mail' : undefined} />}
      onClick={onOpen}
      onLongPress={onMenu}
      title={
        <span className="ibx-row__line">
          <span className={`ibx-row__who${address ? ' ibx-row__who--address' : ''}`}>{who}</span>
          {isUrgent(m) && <span className="ibx-tag ibx-tag--bad">Urgent</span>}
          {hasRole && <RoleChip role={role} roleBy={role.roleBy} spheres={spheres} projects={projects} />}
          {!hasRole && job && (
            <span className="ibx-tag ibx-tag--job" style={{ color: sphereColor(spheres, job) }}>
              {sphereName(spheres, job)}
            </span>
          )}
          <span className="ibx-row__age" title={channelLabel(m.channel)}>
            {ageLabel(item.ageHours)}
          </span>
        </span>
      }
      sub={
        <>
          {!item.senderVerified && <span className="ibx-row__unv">Unverified sender</span>}
          {claimed && <span className="ibx-row__claim">calls itself “{claimed}”</span>}
          <span className="ibx-row__text">{preview}</span>
          {hint && <span className={`ibx-row__hint${hint.tone === 'warn' ? ' ibx-row__hint--warn' : ''}`}>{hint.text}</span>}
        </>
      }
      trailing={
        <>
          <Button size="sm" variant="secondary" onClick={onDone}>
            Done
          </Button>
          <button type="button" className="la-menu" aria-label={`More for ${who}`} onClick={onMenu}>
            <Icon name="more" size={20} />
          </button>
        </>
      }
      swipe={{ leading: { label: 'Done', icon: 'check', tone: 'good', onAction: onDone }, trailing }}
    />
  );
}

/** The thread, in a sheet, with the row's actions at the bottom. */
function ThreadSheet({
  item,
  onClose,
  onAct,
  busy,
  role,
}: {
  item: InboxItem;
  onClose: () => void;
  onAct: (what: 'done' | 'fyi' | 'task') => void;
  busy: boolean;
  /** The role chip block (Personal / a project), tap to change. */
  role?: React.ReactNode;
}): JSX.Element {
  const who = headlineName(item);
  const m = item.message;
  const claimed = showsAddress(item) && m.fromName?.trim() && m.fromName.trim() !== m.handle ? m.fromName.trim() : null;
  const toast = useToast();
  const [filing, setFiling] = useState(false);
  const [saving, setSaving] = useState<{ messageId: number; att: AttachmentInfo } | null>(null);
  const [filedTo, setFiledTo] = useState<string | null>(m.projectId);

  async function fileTo(p: Project): Promise<void> {
    setFiling(false);
    try {
      await triageMessage(m.id, { projectId: p.id });
      setFiledTo(p.id);
      toast.info(`Filed to ${p.name}`);
    } catch (err) {
      toast.error(`Couldn't file it: ${errorText(err)}`);
    }
  }

  const onSave = (messageId: number, att: AttachmentInfo): void => setSaving({ messageId, att });

  return (
    <>
      <Sheet title={who} onClose={onClose} className="ibx-sheet">
        {(!item.senderVerified || claimed) && (
          <p className="ibx-sheet__warn">
            <Icon name="alert" size={16} />
            Unverified sender{claimed ? ` — calls itself “${claimed}”` : ''}. Check the address before replying.
          </p>
        )}
        <p className="ibx-sheet__chan">{channelLabel(m.channel)}</p>
        {role}
        {m.threadKey ? <Thread threadKey={m.threadKey} who={who} onSave={onSave} /> : <Bubbles who={who} items={[m]} onSave={onSave} />}
        <div className="ibx-sheet__actions">
          <Button size="lg" block icon="check" onClick={() => onAct('done')}>
            Done
          </Button>
          <div className="ibx-sheet__more">
            <Button variant="secondary" onClick={() => onAct('fyi')}>
              FYI
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => onAct('task')}>
              Make task
            </Button>
            <Button variant="secondary" icon="folder" onClick={() => setFiling(true)}>
              {filedTo ? 'Filed ✓ — move' : 'File to project'}
            </Button>
          </div>
          <ReviewLink item={item} />
        </div>
      </Sheet>
      {filing && <ProjectPick title="File to project" current={filedTo} onPick={(p) => void fileTo(p)} onClose={() => setFiling(false)} />}
      {saving && (
        <SaveAttachmentSheet
          messageId={saving.messageId}
          att={saving.att}
          onClose={() => setSaving(null)}
          onSaved={(projectName, fileName) => {
            setSaving(null);
            toast.info(`Saved ${fileName} to ${projectName}`);
          }}
        />
      )}
    </>
  );
}

/** Projects as a one-tap list (sub-projects included, closed ones left out). */
function ProjectPick({
  title,
  current,
  onPick,
  onClose,
}: {
  title: string;
  current: string | null;
  onPick: (p: Project) => void;
  onClose: () => void;
}): JSX.Element {
  const projects = useLoad(() => listProjects(), []);
  const live = (projects.data ?? []).filter((p) => p.status !== 'archived' && p.status !== 'done');
  if (!projects.data) {
    return (
      <Sheet title={title} onClose={onClose}>
        {projects.error ? <ErrorState message={projects.error} onRetry={projects.reload} /> : <Loading rows={3} />}
      </Sheet>
    );
  }
  return (
    <PickSheet
      title={title}
      label="Projects"
      value={current}
      options={live.map((p) => ({ key: p.id, name: p.name }))}
      onPick={(id) => {
        const p = live.find((x) => x.id === id);
        if (p) onPick(p);
      }}
      onClose={onClose}
    />
  );
}

/** "Save to project": which project, and optionally a name and a line about it. */
function SaveAttachmentSheet({
  messageId,
  att,
  onClose,
  onSaved,
}: {
  messageId: number;
  att: AttachmentInfo;
  onClose: () => void;
  onSaved: (projectName: string, fileName: string) => void;
}): JSX.Element {
  const projects = useLoad(() => listProjects(), []);
  const live = (projects.data ?? []).filter((p) => p.status !== 'archived' && p.status !== 'done');
  const [projectId, setProjectId] = useState('');
  const [name, setName] = useState(att.name);
  const [about, setAbout] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const p = live.find((x) => x.id === projectId);
    if (!p) return setErr('Pick a project.');
    setBusy(true);
    setErr(null);
    try {
      const body: { projectId: string; name?: string; about?: string } = { projectId: p.id };
      if (name.trim() && name.trim() !== att.name) body.name = name.trim();
      if (about.trim()) body.about = about.trim();
      const file = await saveAttachmentToProject(messageId, att.index, body);
      onSaved(p.name, file?.name ?? (name.trim() || att.name));
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Save to project" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={submit} aria-label="Save attachment to project">
        <ul className="ui-group bc-inset">
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Project</span>
              <select className="bc-field__input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">{projects.data ? 'Choose…' : 'Loading…'}</option>
                {live.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Name</span>
              <input className="bc-field__input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">About</span>
              <input className="bc-field__input" value={about} maxLength={500} placeholder="optional" onChange={(e) => setAbout(e.target.value)} />
            </label>
          </li>
        </ul>
        {(err || projects.error) && (
          <p className="bc-formerr" role="alert">
            {err ?? projects.error}
          </p>
        )}
        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? 'Saving…' : 'Save to project'}
        </Button>
      </form>
    </Sheet>
  );
}

type OnSave = (messageId: number, att: AttachmentInfo) => void;

function Thread({ threadKey, who, onSave }: { threadKey: string; who: string; onSave: OnSave }): JSX.Element {
  const t = useLoad(() => getThread(threadKey), [threadKey]);
  if (t.error) return <ErrorState message={t.error} onRetry={t.reload} />;
  if (!t.data) return <Loading rows={2} />;
  return <Bubbles who={who} items={t.data} onSave={onSave} />;
}

function Bubbles({ who, items, onSave }: { who: string; items: readonly MessageRow[]; onSave: OnSave }): JSX.Element {
  return (
    <div className="ibx-thread">
      {items.map((m) => {
        const atts = attachmentsOf(m);
        return (
          <div key={m.id} className={`ibx-msg ibx-msg--${m.direction}`}>
            <div className="ibx-msg__meta">
              <span className="ibx-msg__who">{m.direction === 'out' ? 'You' : who}</span>
              <span>{stamp(m.sentAt)}</span>
            </div>
            <div className="ibx-msg__bubble">
              {m.subject && <p className="ibx-msg__subject">{m.subject}</p>}
              <p className="ibx-msg__text">{m.text}</p>
            </div>
            {atts.length > 0 && (
              <div className="ibx-att" aria-label="Attachments">
                <p className="ibx-att__title">
                  {atts.length} attachment{atts.length === 1 ? '' : 's'}
                </p>
                {atts.map((a) => (
                  <div key={a.index} className="ibx-att__row">
                    <Icon name="file" size={18} />
                    <span className="ibx-att__name" title={a.name}>
                      {a.name}
                    </span>
                    <Button size="sm" variant="secondary" onClick={() => onSave(m.id, a)}>
                      Save to project
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
